// Records an answer cites, saved as ids: the worker numbers them, the web labels them by lookup.
import type { SpecPresentEntity } from './spec-present.js';

export const SPEC_SOURCES_PART_TYPE = 'spec-sources';

export const SPEC_SOURCE_KINDS = [
  'project',
  'account',
  'contact',
  'web',
  'knowledge',
  'doc',
] as const;
export type SpecSourceKind = (typeof SPEC_SOURCE_KINDS)[number];

/** A web source's id is its URL; longer ones are not cited, so a call's handles fit one frame. */
export const SPEC_WEB_SOURCE_URL_MAX = 1_000;
/** The longest title a web or knowledge source keeps. */
export const SPEC_WEB_SOURCE_TITLE_MAX = 200;
/** A docs source's id is the page's site path, as the bundle names it. */
export const SPEC_DOC_SOURCE_PATH = /^[a-z0-9-]+(?:\/[a-z0-9-]+)*$/;
export const SPEC_DOC_SOURCE_PATH_MAX = 200;
/** The longest URL web_search reads; a longer one is refused. */
export const SPEC_WEB_URL_MAX_CHARS = 2_048;
/** How many of the member's earlier URLs a run carries for web_search's reads, newest first. */
export const SPEC_MEMBER_URLS_MAX = 200;

const URL_IN_PROSE = /https?:\/\/[^\s"'<>\\`]+/g;

/** Each http(s) URL a text names, as typed (trailing punctuation included). */
export function specUrlsInProse(text: string): string[] {
  return [...text.matchAll(URL_IN_PROSE)].map((match) => match[0]);
}

/** Sources one message can carry; markers past this are removed. */
export const SPEC_SOURCES_MAX = 100;

export interface SpecSource {
  n: number;
  kind: SpecSourceKind;
  /** A record's or knowledge page's uuid, a web page's URL as the search returned it, or a docs page's path. */
  id: string;
  /** A web, knowledge or docs page's title; records are named by lookup. */
  title?: string;
  /** The knowledge section cited, when a search found it. */
  section?: string;
}

export interface SpecSourcesPart {
  type: typeof SPEC_SOURCES_PART_TYPE;
  sources: SpecSource[];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ENTITY_BY_KIND: Record<SpecSourceKind, SpecPresentEntity | null> = {
  project: 'projects',
  account: 'accounts',
  contact: 'contacts',
  web: null,
  knowledge: null,
  doc: null,
};

/** The lookup entity a cited record is named by; null for a web, knowledge or docs page, which carries its title. */
export function specSourceEntity(kind: SpecSourceKind): SpecPresentEntity | null {
  return ENTITY_BY_KIND[kind];
}

/** Whether an id is valid for its kind: an http(s) URL for a web page, a site path for a docs page, else a uuid. */
function isSourceId(kind: SpecSourceKind, id: unknown): id is string {
  if (typeof id !== 'string') return false;
  if (kind === 'doc') return id.length <= SPEC_DOC_SOURCE_PATH_MAX && SPEC_DOC_SOURCE_PATH.test(id);
  if (kind !== 'web') return UUID.test(id);
  return id.length <= SPEC_WEB_SOURCE_URL_MAX && /^https?:\/\/[^\s]+$/i.test(id);
}

/** Whether a kind carries its own title rather than being named by lookup. */
export function specSourceHasTitle(kind: SpecSourceKind): boolean {
  return kind === 'web' || kind === 'knowledge' || kind === 'doc';
}

/** The title and knowledge section a source keeps; anything else is dropped. */
function carried(
  kind: SpecSourceKind,
  title: unknown,
  section: unknown,
): Pick<SpecSource, 'title' | 'section'> {
  const out: Pick<SpecSource, 'title' | 'section'> = {};
  if (specSourceHasTitle(kind) && typeof title === 'string' && title.trim() !== '') {
    out.title = title.trim().slice(0, SPEC_WEB_SOURCE_TITLE_MAX);
  }
  if (kind === 'knowledge' && typeof section === 'string' && UUID.test(section))
    out.section = section;
  return out;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A saved sources part, leniently: bad entries and repeated numbers are skipped. */
export function readSpecSources(raw: unknown): SpecSource[] {
  if (!isRecord(raw) || raw.type !== SPEC_SOURCES_PART_TYPE || !Array.isArray(raw.sources)) {
    return [];
  }
  const sources: SpecSource[] = [];
  const seen = new Set<number>();
  for (const entry of raw.sources) {
    if (sources.length >= SPEC_SOURCES_MAX) break;
    if (!isRecord(entry)) continue;
    const { n, kind, id, title, section } = entry;
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 1 || seen.has(n)) continue;
    if (!SPEC_SOURCE_KINDS.includes(kind as SpecSourceKind)) continue;
    if (!isSourceId(kind as SpecSourceKind, id)) continue;
    seen.add(n);
    sources.push({
      n,
      kind: kind as SpecSourceKind,
      id,
      ...carried(kind as SpecSourceKind, title, section),
    });
  }
  return sources;
}

/** A record's handle in one run (`S3`), as the model saw it beside the record. */
export interface SpecCitationHandle {
  handle: string;
  kind: SpecSourceKind;
  id: string;
  /** A web or knowledge page's title, carried so a resumed run can still name the source. */
  title?: string;
  section?: string;
}

const CITE_HANDLE = /^S[1-9]\d{0,3}$/;

/** `citesJson` on a `tool_call` frame: one call's handles fit (100 at most), a longer one is dropped. */
export const SPEC_CITES_JSON_MAX = 8_192;

/** A call's new handles as its `tool_call` event carries them: ids, a title, a knowledge section. */
export function serializeSpecCites(handles: readonly SpecCitationHandle[]): string | undefined {
  if (handles.length === 0) return undefined;
  return JSON.stringify(handles.map(specCiteTuple));
}

/** One handle as the cites frame carries it. */
export function specCiteTuple(entry: SpecCitationHandle): string[] {
  const title = specSourceHasTitle(entry.kind) ? (entry.title ?? '') : '';
  const section = entry.kind === 'knowledge' ? (entry.section ?? '') : '';
  if (section) return [entry.handle, entry.kind, entry.id, title, section];
  return title ? [entry.handle, entry.kind, entry.id, title] : [entry.handle, entry.kind, entry.id];
}

export function readSpecCites(raw: unknown): SpecCitationHandle[] {
  if (typeof raw !== 'string') return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((entry): SpecCitationHandle[] => {
    if (!Array.isArray(entry)) return [];
    const [handle, kind, id, title, section] = entry as unknown[];
    if (typeof handle !== 'string' || !CITE_HANDLE.test(handle)) return [];
    if (!SPEC_SOURCE_KINDS.includes(kind as SpecSourceKind)) return [];
    if (!isSourceId(kind as SpecSourceKind, id)) return [];
    return [
      {
        handle,
        kind: kind as SpecSourceKind,
        id,
        ...carried(kind as SpecSourceKind, title, section),
      },
    ];
  });
}

export type SpecCitationSegment =
  | { type: 'text'; text: string }
  | { type: 'cite'; n: number }
  | { type: 'live'; handle: string };

// `[S3]` or `[S1, S2]` as the model writes them; `[3]` once numbered.
const MARKER = /\[(?:(S[1-9]\d{0,3}(?:[ \t]*,[ \t]*S[1-9]\d{0,3})*)|([1-9]\d{0,2}))\]/g;
const HANDLE = /S[1-9]\d{0,3}/g;

/** A marker that is really a link (`[1](url)`) or a definition (`[1]: url` at line start). */
function isLinkSyntax(text: string, start: number, end: number): boolean {
  const next = text[end];
  if (next === '(') return true;
  if (next !== ':') return false;
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  return /^ {0,3}$/.test(text.slice(lineStart, start));
}

/** Prose split into text and citation markers; the caller keeps code and links out of it. */
export function splitCitationMarkers(text: string): SpecCitationSegment[] {
  const segments: SpecCitationSegment[] = [];
  const pushText = (value: string): void => {
    if (value.length === 0) return;
    const last = segments.at(-1);
    if (last?.type === 'text') last.text += value;
    else segments.push({ type: 'text', text: value });
  };
  let cursor = 0;
  for (const match of text.matchAll(MARKER)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (isLinkSyntax(text, start, end)) continue;
    pushText(text.slice(cursor, start));
    if (match[1]) {
      for (const handle of match[1].match(HANDLE) ?? []) segments.push({ type: 'live', handle });
    } else {
      segments.push({ type: 'cite', n: Number(match[2]) });
    }
    cursor = end;
  }
  pushText(text.slice(cursor));
  return segments;
}

const FENCE = /^ {0,3}(`{3,}|~{3,})/;

/** Applies `rewrite` to prose only: code blocks and inline code spans pass through. */
function mapProse(text: string, rewrite: (prose: string) => string): string {
  const out: string[] = [];
  let prose = '';
  let fence: string | null = null;
  let blankBefore = true;
  let indented = false;
  for (const line of text.split(/(?<=\n)/)) {
    const marks = FENCE.exec(line)?.[1];
    if (fence) {
      out.push(line);
      if (
        marks &&
        marks[0] === fence[0] &&
        marks.length >= fence.length &&
        !line.trim().slice(marks.length)
      ) {
        fence = null;
      }
      continue;
    }
    if (marks) {
      out.push(mapInline(prose, rewrite), line);
      prose = '';
      fence = marks;
      indented = false;
      blankBefore = false;
      continue;
    }
    if (/^[ \t]*(?:\r?\n)?$/.test(line)) {
      if (indented) out.push(line);
      else prose += line;
      blankBefore = true;
      continue;
    }
    if (/^(?: {4}|\t)/.test(line) && (blankBefore || indented)) {
      out.push(mapInline(prose, rewrite), line);
      prose = '';
      indented = true;
      blankBefore = false;
      continue;
    }
    indented = false;
    blankBefore = false;
    prose += line;
  }
  out.push(mapInline(prose, rewrite));
  return out.join('');
}

function mapInline(text: string, rewrite: (prose: string) => string): string {
  const runs = [...text.matchAll(/`+/g)];
  let out = '';
  let start = 0;
  for (let i = 0; i < runs.length; i += 1) {
    const open = runs[i];
    if (!open || (open.index ?? 0) < start) continue;
    const closeAt = runs.findIndex((run, j) => j > i && run[0].length === open[0].length);
    const close = runs[closeAt];
    if (!close) continue;
    const end = (close.index ?? 0) + close[0].length;
    out += rewrite(text.slice(start, open.index)) + text.slice(open.index, end);
    start = end;
    i = closeAt;
  }
  return out + rewrite(text.slice(start));
}

export type SpecCitationMarker = Exclude<SpecCitationSegment, { type: 'text' }>;

/** Markers replaced (or dropped on null); a dropped one takes its leading space with it. */
function rewriteProse(
  prose: string,
  replace: (marker: SpecCitationMarker) => string | null,
): string {
  let out = '';
  let last: string | null = null;
  let dropped = false;
  const append = (value: string) => {
    let text = value;
    if (dropped) {
      if (/[ \t]$/.test(out) && /^(\s|[.,;:!?)\]]|$)/.test(text)) out = out.replace(/[ \t]+$/, '');
      else if (out === '' || out.endsWith('\n')) text = text.replace(/^[ \t]+/, '');
      dropped = false;
    }
    out += text;
  };
  for (const segment of splitCitationMarkers(prose)) {
    if (segment.type === 'text') {
      append(segment.text);
      last = null;
      continue;
    }
    const marker = replace(segment);
    if (marker === null) dropped = true;
    else if (marker !== last) {
      append(marker);
      last = marker;
    }
  }
  if (dropped) append('');
  return out;
}

/** Markdown with each marker replaced (null drops it), code and code blocks untouched. */
export function rewriteCitationMarkers(
  text: string,
  replace: (marker: SpecCitationMarker) => string | null,
): string {
  return mapProse(text, (prose) => rewriteProse(prose, replace));
}

/** Text with every marker removed: history, titles and copied answers. */
export function stripCitationMarkers(text: string): string {
  return rewriteCitationMarkers(text, () => null);
}
